import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Link } from "@quintype/components";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { STATIC_KEYWORDS } from "../../../../constants";
import { filterStoriesByForum } from "../../../../utils/forum-filter";
import { filterStoriesByTemplate } from "../../../../utils/story-utils";
import { getLinkTarget } from "../../../../arrow/utils/utils";

import './AlsoReadSection.m.css';

const AlsoReadCard = ({ story }) => {
  const [svgUrl, setSvgUrl] = useState("");

  const headline = get(story, ["alternative", "home", "default", "headline"], "") || get(story, ["headline"], "");
  const slug = get(story, ["slug"], "");
  const lastPublishedAt = get(story, ["last-published-at"], null);

  const tags = get(story, ["tags"], []);
  const reportTypeTag = tags.find(tag => tag.type === "report-type" || tag["tag-type"] === "Entity");

  useEffect(() => {
    if (reportTypeTag?.id) {
      fetch(`/api/v1/entities/${reportTypeTag.id}`)
        .then(res => res.json())
        .then(data => {
          const url = data?.entity?.["desktop-svg"]?.[0]?.url;
          if (url) setSvgUrl(url);
        })
        .catch(err => console.error("Error fetching entity:", err));
    }
  }, [reportTypeTag?.id]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const svgIcon = useMemo(() => {
    if (svgUrl) return svgUrl;
    const linkedEntity = get(story, ["linked-entities", 0], null);
    if (linkedEntity) {
      const desktopSvg = get(linkedEntity, ["desktop-svg", 0, "url"], "");
      if (desktopSvg) return desktopSvg;
    }
    return "";
  }, [svgUrl, story]);

  return (
    <Link
      href={`/${slug}`}
      styleName="also-read-card"
      target={getLinkTarget(`/${slug}`)}
      rel="noopener"
    >
      <div styleName="also-read-content">
        {svgIcon && (
          <div styleName="entity-icon">
            <img src={svgIcon} alt="" />
          </div>
        )}
        <div styleName="card-text">
          <h3 styleName="also-read-headline">{headline}</h3>
          {lastPublishedAt && (
            <div styleName="also-read-timestamp">
              {formatTimestamp(lastPublishedAt)}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

AlsoReadCard.propTypes = {
  story: PropTypes.object.isRequired,
};

const AlsoReadSection = ({ storyId, isLeadershipFocus }) => {
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));

  const [allRelatedStories, setAllRelatedStories] = useState([]);

  // Check if we're still loading metadata for logged-in users
  const isMetadataLoading = member && subscriberMetadata === null;

  // Apply forum-based and template filtering to related stories
  const filteredRelatedStories = useMemo(() => {
    if (!allRelatedStories || allRelatedStories.length === 0) return [];

    // First filter by template
    const templateFiltered = filterStoriesByTemplate(allRelatedStories);

    // Convert stories to the format expected by filterStoriesByForum
    const storyItems = templateFiltered.map(story => ({
      type: "story",
      story: story
    }));

    const filtered = filterStoriesByForum(storyItems, member, subscriberMetadata);

    // Extract the stories from the filtered result and limit to 2
    return filtered.map(item => item.story).slice(0, 2);
  }, [allRelatedStories, member, subscriberMetadata]);

  useEffect(() => {
    if (!storyId) return;

    const fetchRelatedStories = async () => {
      try {
        const response = await fetch(`/api/v1/stories/${storyId}/related-stories`);
        if (!response.ok) throw new Error("Failed to fetch related stories");
        const data = await response.json();

        const relatedStoriesArray = data["related-stories"] || [];

        // Sort descending by last-published-at, latest first
        const sortedStories = relatedStoriesArray.sort(
          (a, b) => new Date(b["last-published-at"]) - new Date(a["last-published-at"])
        );

        // Store all stories for filtering (don't limit here)
        setAllRelatedStories(sortedStories);
      } catch (error) {
        console.error("Error fetching related stories:", error);
        setAllRelatedStories([]);
      }
    };

    fetchRelatedStories();
  }, [storyId]);

  // Don't show anything if metadata is still loading
  if (isMetadataLoading) return null;

  // Don't show anything if no filtered stories after forum filtering
  if (!filteredRelatedStories.length) return null;

  return (
    <div styleName={`also-read-section ${isLeadershipFocus ? 'leadership-also-read' : ''}`} className="hide-element">
      <div styleName="also-read-wrapper">
        <h2 styleName="also-read-title">{STATIC_KEYWORDS.ALSO_READ_TITLE}</h2>
        <div styleName="also-read-cards">
          {filteredRelatedStories.map((story) => (
            <AlsoReadCard key={story.id} story={story} />
          ))}
        </div>
      </div>
    </div>
  );
};

AlsoReadSection.propTypes = {
  storyId: PropTypes.string.isRequired,
  isLeadershipFocus: PropTypes.bool,
};

export default AlsoReadSection;
